import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { HardDrive, ShieldAlert } from "lucide-react";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1630288214173-a119cf823388?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxleGNhdmF0b3IlMjBjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc4NzE0OTExMnww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const denied = location.state?.denied;
  const deniedMessage = location.state?.message;

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={LOGIN_BG} alt="Saha" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-zinc-950/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-sm flex items-center justify-center overflow-hidden p-0.5">
              <img src="/rayem-logo.png" alt="RAYEM" className="w-full h-full object-contain" />
            </div>
            <span className="font-display font-black text-lg tracking-tight">RAYEM MAKİNE &amp; STOK TAKİP</span>
          </div>
          <div>
            <h2 className="font-display font-black text-4xl xl:text-5xl leading-none tracking-tight max-w-md">
              Sahadaki her makine, tek panelde.
            </h2>
            <p className="text-zinc-300 mt-4 max-w-md text-base">
              Lokasyon, çalışma durumu, bakım geçmişi, yedek parça stoğu ve ekipman zimmetini
              gerçek zamanlı takip edin.
            </p>
          </div>
          <div className="text-xs uppercase tracking-[0.15em] text-zinc-500">
            Araç · Makine · Stok · Zimmet
          </div>
        </div>
      </div>

      {/* Right: login */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-white px-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-white border border-zinc-200 rounded-sm flex items-center justify-center overflow-hidden p-0.5">
              <img src="/rayem-logo.png" alt="RAYEM" className="w-full h-full object-contain" />
            </div>
            <span className="font-display font-black text-lg tracking-tight">RAYEM MAKİNE &amp; STOK TAKİP</span>
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-primary mb-3">Giriş</p>
          <h1 className="font-display font-black text-3xl tracking-tight leading-none text-zinc-950">
            Operasyon paneline hoş geldiniz
          </h1>
          <p className="text-muted-foreground mt-3 text-sm">
            Devam etmek için şirket Google hesabınızla giriş yapın.
          </p>

          {denied && (
            <div data-testid="access-denied-banner" className="mt-6 flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 rounded-sm px-4 py-3">
              <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
              <p className="text-sm font-medium">{deniedMessage || "Üzgünüm, bu sistemi kullanmak için yetkiniz bulunmamaktadır. Şirket yöneticinizle iletişime geçiniz."}</p>
            </div>
          )}

          <button
            data-testid="login-google-btn"
            onClick={handleLogin}
            className="mt-8 w-full flex items-center justify-center gap-3 bg-zinc-950 hover:bg-zinc-800 text-white font-semibold py-3.5 rounded-sm transition-colors duration-200"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.3C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.2 5.3C41.4 36.5 44 30.8 44 24c0-1.3-.1-2.3-.4-3.5z"/>
            </svg>
            Google ile Giriş Yap
          </button>

          <p className="text-xs text-muted-foreground mt-6 text-center">
            Şirket içi kullanıma özeldir.
          </p>
        </div>
      </div>
    </div>
  );
}
